import { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation, Link } from 'react-router-dom';
import { usePieces } from '../hooks/usePieces';
import RelatedModal from '../components/RelatedModal';

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { pieces, loading: piecesLoading } = usePieces();
  const piece = pieces.find(p => p.id === id);
  const fromCollection = location.state?.fromCollection;

  const [phase, setPhase] = useState(0);
  const [, setBlackOverlay] = useState(fromCollection);
  const [selectedImageIdx, setSelectedImageIdx] = useState(0);
  const [imgKey, setImgKey] = useState(0);
  const [modalPiece, setModalPiece] = useState(null);

  useEffect(() => {
    setSelectedImageIdx(0);
    setImgKey(k => k + 1);
  }, [id]);

  useEffect(() => {
    if (fromCollection) {
      setPhase(2);
      setBlackOverlay(true);
      const t1 = setTimeout(() => setBlackOverlay(false), 100);
      return () => clearTimeout(t1);
    }
    setPhase(0);
    const t1 = setTimeout(() => setPhase(1), 50);
    const t2 = setTimeout(() => setPhase(2), 800);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [id, fromCollection]);

  if (piecesLoading) {
    return <div className="h-screen bg-black" />;
  }

  if (!piece) {
    return (
      <div className="h-screen flex items-center justify-center bg-black">
        <div className="text-center">
          <p className="font-serif text-2xl text-white mb-4">Pièce introuvable</p>
          <Link to="/collection" className="text-sm text-white/40 hover:text-white transition-colors underline">
            Retour à la collection
          </Link>
        </div>
      </div>
    );
  }

  const mainImg = piece.images?.[selectedImageIdx] || piece.images?.[0] || piece.img;

  // Pièces voisines (wrap modulo length) : prev, next, next+1
  const related = piece.relatedProducts || [];

  const handleImageChange = (i) => {
    setSelectedImageIdx(i);
    setImgKey(k => k + 1);
  };

  return (
    <div className="h-screen overflow-hidden bg-black relative">

      {/* ── IMAGE PLEIN ECRAN ── */}
      <img
        key={`img-${imgKey}`}
        src={mainImg}
        alt={piece.name}
        className="absolute inset-0 w-full h-full object-cover transition-all duration-[1.5s] ease-out animate-imgReveal"
        style={{
          transform: phase >= 1 ? 'scale(1)' : 'scale(1.15)',
          opacity: phase >= 1 ? 1 : 0,
          filter: 'none',
        }}
      />

      {/* Vignette très légère */}
      <div className="absolute inset-0" style={{ boxShadow: 'inset 0 0 200px 80px rgba(0,0,0,0.05)' }} />

      {/* ── BOUTON RETOUR — haut gauche ── */}
      <button
        onClick={() => navigate('/collection')}
        className={`absolute top-24 left-8 md:left-12 flex items-center gap-3 text-white/50 hover:text-white transition-all z-10 group duration-700 ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
        <span className="w-8 h-px bg-current transition-all duration-300 group-hover:w-14" />
        <span className="text-[11px] tracking-[0.2em] uppercase">Retour</span>
      </button>

      {/* ── NOM — grand, positionné en haut ── */}
      <div className={`absolute top-1/4 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}>
        <h1 className="font-serif text-4xl md:text-6xl lg:text-7xl text-white font-light leading-none tracking-wide">
          {piece.name || 'Titre à définir'}
        </h1>
      </div>

      {/* ── HISTOIRE — centre gauche ── */}
      <div className={`absolute top-1/2 left-8 md:left-12 lg:left-16 -translate-y-1/2 z-10 max-w-md transition-all duration-[1.2s] delay-200 ease-out ${phase >= 2 ? 'opacity-100 translate-y-[-50%]' : 'opacity-0 translate-y-[-40%]'}`}
        style={{ marginTop: '60px' }}>
        <p className="text-white/50 text-[15px] leading-[1.9] font-light">
          {piece.story}
        </p>
      </div>

      {/* ── DETAILS PIERRE — bas gauche ── */}
      <div className={`absolute bottom-36 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] delay-400 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'}`}>
        <div className="flex gap-10 flex-wrap">
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Pierre</p>
            <p className="font-serif text-lg text-white/80">{piece.stone || '—'}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Origine</p>
            <p className="font-serif text-lg text-white/80">{piece.origin || '—'}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Taille</p>
            <p className="font-serif text-lg text-white/80">{piece.size || '—'}</p>
          </div>
          <div>
            <p className="text-[9px] tracking-[0.3em] uppercase text-white/25 mb-1">Disponibilité</p>
            <p className="font-serif text-lg text-white/80">
              {piece.available > 0 ? `${piece.available} pièce${piece.available > 1 ? 's' : ''}` : 'Sur commande'}
            </p>
          </div>
        </div>
      </div>

      {/* ── CTA — bas gauche sous les détails ── */}
      <div className={`absolute bottom-10 left-8 md:left-12 lg:left-16 z-10 transition-all duration-[1.2s] delay-500 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        <Link to={`/contact?piece=${encodeURIComponent(piece.name)}&pierre=${encodeURIComponent(piece.stone || '')}`}
          className="inline-block px-8 py-3 border border-white/20 text-white/70 text-[11px] tracking-[0.25em] uppercase hover:bg-white hover:text-black transition-all duration-500">
          Nous écrire
        </Link>
      </div>

      {/* ── CARROUSEL MINIATURES IMAGES — bas centre ── */}
      {piece.images?.length > 1 && (
        <div className={`absolute bottom-16 left-1/2 -translate-x-1/2 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          <div className="flex gap-3">
            {piece.images.map((img, i) => {
              const active = i === selectedImageIdx;
              return (
                <button key={i} onClick={() => handleImageChange(i)}
                  className="overflow-hidden border transition-all duration-300 w-20 h-20 md:w-24 md:h-24"
                  style={{
                    borderColor: active ? '#C8A96E' : 'rgba(255,255,255,0.15)',
                    opacity: active ? 1 : 0.5,
                  }}>
                  <img src={img} alt=""
                    className="w-full h-full object-cover"
                    style={{ filter: active ? 'none' : 'brightness(0.7)' }} />
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* ── BIJOUX LIÉS — bas droite ── */}
      {related.length > 0 && (
        <div className={`absolute bottom-16 right-8 md:right-12 lg:right-16 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-4'}`}>
          <p className="text-[10px] tracking-[0.3em] uppercase text-white/40 mb-3 text-right">
            Irait bien avec
          </p>
          <div className="flex gap-3 justify-end">
            {related.map((r, i) => (
              <button key={`${r.id}-${i}`}
                onClick={() => setModalPiece(r)}
                className="group flex flex-col items-center gap-2 transition-all duration-300">
                <div className="w-20 h-20 md:w-24 md:h-24 overflow-hidden border border-white/15 group-hover:border-white/40 transition-all duration-300">
                  <img src={r.img} alt={r.name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" />
                </div>
                <p className="text-[10px] tracking-[0.2em] uppercase text-white/50 group-hover:text-white transition-colors duration-300">
                  {r.stone || r.name}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* ── MODALE BIJOU LIÉ ── */}
      <RelatedModal piece={modalPiece} onClose={() => setModalPiece(null)} />

      {/* ── NUMÉRO DE PIÈCE — bas centre ── */}
      <div className={`absolute bottom-4 left-1/2 -translate-x-1/2 z-10 transition-all duration-[1.2s] delay-300 ease-out ${phase >= 2 ? 'opacity-100' : 'opacity-0'}`}>
        <p className="text-[10px] tracking-[0.4em] text-white/15 uppercase">
          {String(piece.num || 0).padStart(2, '0')} / {String(pieces.length).padStart(2, '0')}
        </p>
      </div>

      <style>{`
        @keyframes imgReveal {
          from { opacity: 0; transform: scale(1.05); }
          to { opacity: 1; transform: scale(1); }
        }
        .animate-imgReveal {
          animation: imgReveal 0.7s cubic-bezier(0.22, 1, 0.36, 1) forwards;
        }
      `}</style>
    </div>
  );
}
